import { WithContext, Product as ProductSchema } from 'schema-dts';

import ContactConfig from 'types/ContactConfig';
import { ImagePreset } from 'types/ImagePreset';
import resolveImageUrl from 'utils/resolveImageUrl';
import getRelativeDate from 'utils/getRelativeDate';
import map from 'utils/map';

import { Props as PageProps } from '../Model.types';

const structuredDataImagePresets: ImagePreset[] = ['p_thumb_1x1', 'p_thumb_4x3', 'p_thumb_16x9'];

const getTomorrowDate = (): string => getRelativeDate(1);

const getModelAvailability = ({ availability, isEol }: PageProps['variant'], isConfigurable?: boolean) => {
  if (isEol) {
    return 'https://schema.org/Discontinued';
  }

  if (isConfigurable) {
    return 'https://schema.org/BackOrder';
  }

  return availability ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock';
};

export const getModelStructuredData = (
  { product, variant, variants }: Pick<PageProps, 'product' | 'variant' | 'variants'>,
  appUrl: string,
  contact: ContactConfig
): WithContext<ProductSchema> => {
  const url = `${appUrl}/model/${product.urlSlug}/${product.id}`;
  const image = product.thumbnail;
  const { isConfigurable } = product;
  const productAvailability = getModelAvailability(variant, isConfigurable);
  const mappedVariants = map<(typeof variants)[number], (typeof variants)[number]['variant']>(variants, 'variant');
  const [shipsUntil] = <[Nullable<string>]>map(mappedVariants, 'shipsUntil').sort();
  const currency = mappedVariants[0]?.price.currency || 'PLN';
  const sortedByPriceVariants = map<(typeof mappedVariants)[number], number>(mappedVariants, 'grossPrice').sort(
    (a, b) => a - b
  );
  const lowestPrice = isConfigurable ? variant.grossPrice : sortedByPriceVariants[0];
  const highestPrice = sortedByPriceVariants.at(-1);

  const structuredData: WithContext<ProductSchema> = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: product.name,
    category: product.type.name,
    image: image
      ? structuredDataImagePresets.map((preset) =>
          resolveImageUrl(
            {
              preset,
              extension: image.extension,
              name: image.basename,
              productName: product.name,
            },
            { appUrl }
          )
        )
      : undefined,
    model: product.name,
    url,
    aggregateRating: product.rating
      ? {
          '@type': 'AggregateRating',
          ratingValue: product.rating,
          ratingCount: product.ratingCount,
        }
      : undefined,
    brand: product.manufacturer?.name
      ? {
          '@type': 'Brand',
          name: product.manufacturer.name,
        }
      : undefined,
    offers: {
      '@type': 'AggregateOffer',
      url,
      lowPrice: lowestPrice / 100,
      highPrice: isConfigurable ? undefined : (highestPrice || 0) / 100,
      offerCount: isConfigurable ? undefined : variants.length,
      priceCurrency: currency,
      priceValidUntil: getTomorrowDate(),
      itemCondition: 'https://schema.org/NewCondition',
      availability: productAvailability,
      seller: {
        '@type': 'Organization',
        name: contact.appName,
      },
      shippingDetails: {
        '@type': 'OfferShippingDetails',
        shippingDestination: {
          '@type': 'DefinedRegion',
          addressCountry: 'PL',
        },
        shippingRate: {
          '@type': 'MonetaryAmount',
          value: 0,
          currency: 'PLN',
        },
        deliveryTime: {
          '@type': 'ShippingDeliveryTime',
          businessDays: {
            '@type': 'OpeningHoursSpecification',
            dayOfWeek: [
              'https://schema.org/Monday',
              'https://schema.org/Tuesday',
              'https://schema.org/Wednesday',
              'https://schema.org/Thursday',
              'https://schema.org/Friday',
            ],
          },
          cutoffTime: shipsUntil || '',
          handlingTime: {
            '@type': 'QuantitativeValue',
            minValue: 0,
            maxValue: 1,
            unitCode: 'd',
          },
          transitTime: {
            '@type': 'QuantitativeValue',
            minValue: 1,
            maxValue: 1,
            unitCode: 'd',
          },
        },
      },
    },
  };
  return structuredData;
};
